import { useMemo } from 'react';
import {
  useListOrdersQuery,
  type OrderListQuery,
  type OrderRow,
} from '@/Redux/Slices/operationsApi';
import { useNow } from './shared';

/**
 * The one read behind "a guest order has stopped for a waiter", and the one
 * definition of what makes one of them late.
 *
 * It is a module rather than two call sites because the waiter station now
 * needs this count on its tab strip while `AwaitingConfirmationLane` draws the
 * cards themselves inside a panel. Two hand-written `useListOrdersQuery` calls
 * would work right up until one of them was edited: `listOrders` is keyed by
 * its arguments, so a `limit` changed in one place and not the other silently
 * opens a SECOND cache entry — a second request, a second list, and a badge
 * that counts different orders from the panel it is pointing at. Built here,
 * the two callers cannot disagree, and the second subscriber costs no request.
 */
export function awaitingParams(branchId?: string): OrderListQuery {
  return { awaitingConfirmation: true, branchId, limit: 50 };
}

export interface AwaitingOrders {
  /** Only the rows the server itself flagged — see the filter below. */
  rows: OrderRow[];
  /** How many of those are past the deadline the server stamped on them. */
  lapsed: number;
  isLoading: boolean;
  error: unknown;
  /** The ticking clock the deadline is measured against, so callers share one. */
  now: number;
}

export function useAwaitingOrders(
  branchId?: string,
  options: { skip?: boolean } = {},
): AwaitingOrders {
  const now = useNow(1000);
  const params = useMemo(() => awaitingParams(branchId), [branchId]);
  const { data, isLoading, error } = useListOrdersQuery(params, {
    skip: options.skip,
    refetchOnMountOrArgChange: true,
  });

  /**
   * Only what the server has actually flagged as waiting — never "an order that
   * looks like it might be".
   *
   * The filter reads as redundant against `?awaitingConfirmation=true` and is
   * not. It is what makes `applyOrderEvent` able to drop a card the instant
   * `ORDER_SENT_TO_KITCHEN` arrives, with no round trip: clearing the flag is
   * the whole removal. And it is the guard that keeps this surface from ever
   * becoming a second general order list — an alarm that can be raised by rows
   * the server never called urgent is an alarm people learn to ignore.
   */
  const rows = useMemo(
    () => (data?.rows ?? []).filter((row) => row.awaitingWaiterConfirmation === true),
    [data?.rows],
  );

  const lapsed = useMemo(
    () => rows.filter((row) => isLapsed(row.confirmationDueAt, now)).length,
    [rows, now],
  );

  return { rows, lapsed, isLoading, error, now };
}

/**
 * Whether this wait has run out, asked of the server's stamp and nothing else.
 *
 * There is no threshold constant here on purpose. The SLA is a venue setting
 * (`ordering.confirmationSlaSeconds`) that a manager can change from a settings
 * screen while this board is open; a number compiled into the dashboard would
 * go on reddening cards against the old rule until somebody redeployed it, and
 * would be silently wrong in every venue that picked a different one. A null
 * stamp means the server is not claiming a deadline — so neither is this, and
 * the card simply carries its clock without an alarm.
 */
export function isLapsed(dueAt: string | null | undefined, now: number): boolean {
  if (!dueAt) return false;
  const due = Date.parse(dueAt);
  return Number.isFinite(due) && now >= due;
}
